import React, { useState, useRef, useEffect } from 'react';
import { SketchPicker } from 'react-color';
import PropTypes from 'prop-types';
import hexRgb from 'hex-rgb';

import {
  StyledSwatch,
  StyledColor,
  StyledInput,
  StyledPopover,
  StyledCover,
  ControlWrapper
} from './ColorPicker.styled';

import { rgbaToHex } from 'utils/utils';

const HEX_REGEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const ColorPicker = ({ color, onChange }) => {
  const valueRef = useRef();
  const [isValid, setIsValid] = useState(true);
  const [currentColor, setCurrentColor] = useState(color);
  const [showColorPicker, setShowColorPicker] = useState(false);

  useEffect(() => {
    setCurrentColor(color);
    valueRef.current.value = color;
  }, [color, setCurrentColor]);

  const hexToRgb = color => {
    const { red, green, blue, alpha } = hexRgb(color);

    return {
      r: red,
      g: green,
      b: blue,
      a: alpha
    };
  };

  const handleClick = () => setShowColorPicker(!showColorPicker);

  const handleClose = () => setShowColorPicker(false);

  // we cannot mock SketchPicker atm
  // https://github.com/casesandberg/react-color/issues/745

  /* istanbul ignore next */
  const handleChange = colorObj => {
    const hexWithAlpha = rgbaToHex(colorObj.rgb);
    valueRef.current.value = hexWithAlpha;
    setCurrentColor(hexWithAlpha);
    setIsValid(true);
    onChange(hexWithAlpha);
  };

  const handleInputChange = e => {
    e.preventDefault();

    const color = e.target.value.toUpperCase();

    if (HEX_REGEX.test(color)) {
      setCurrentColor(color);
      setIsValid(true);
      onChange(color);
    } else {
      if (!color) valueRef.current.value = '#';
      setIsValid(false);
    }
  };

  const handleBlur = e => {
    e.preventDefault();

    if (!isValid) {
      setIsValid(true);
      valueRef.current.value = currentColor;
    }
  };

  return (
    <div>
      <ControlWrapper valid={isValid} data-testid="color-picker-wrapper">
        <StyledSwatch
          onClick={handleClick}
          data-testid="color-picker-current-color"
        >
          <StyledColor color={hexToRgb(currentColor)} />
        </StyledSwatch>
        <StyledInput
          type="text"
          maxLength={9}
          ref={valueRef}
          onBlur={handleBlur}
          onClick={handleClick}
          onChange={handleInputChange}
          data-testid="color-picker-input"
        />
      </ControlWrapper>

      {showColorPicker && (
        <StyledPopover data-testid="color-picker-popover">
          <StyledCover data-testid="color-picker-cover" onClick={handleClose}>
            <span className="visually-hidden">Close Color Picker</span>
          </StyledCover>
          <SketchPicker
            color={hexToRgb(currentColor)}
            onChange={handleChange}
          />
        </StyledPopover>
      )}
    </div>
  );
};

ColorPicker.propTypes = {
  color: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

export default ColorPicker;
